// Grid Baseline API client — baseline summaries, rankings, state baselines

import { API } from "./api";

// ─── Types ───────────────────────────────────────────────────────────

export interface BaselineSummary {
  ba_code: string;
  period_type: string;
  period_start: string;
  period_end: string;
  hours_with_data: number;
  total_hours: number;
  data_completeness: number;
  total_generation_mwh: number | null;
  carbon_intensity_kg_per_mwh: number | null;
  human_health_dalys_per_mwh: number | null;
  ecosystem_quality_species_yr_per_mwh: number | null;
  resource_scarcity_usd_per_mwh: number | null;
}

export interface BARanking {
  rank: number;
  ba_code: string;
  carbon_intensity_kg_per_mwh: number | null;
  human_health_dalys_per_mwh: number | null;
  ecosystem_quality_species_yr_per_mwh: number | null;
  resource_scarcity_usd_per_mwh: number | null;
  total_generation_mwh: number | null;
  data_completeness: number;
}

export interface BARankingResponse {
  rankings: BARanking[];
  metric: string;
  as_of: string;
  count: number;
}

export interface StateBaseline {
  state_id: string;
  state_name: string;
  ba_codes: string[];
  carbon_intensity_kg_per_mwh: number | null;
  human_health_dalys_per_mwh: number | null;
  ecosystem_quality_species_yr_per_mwh: number | null;
  resource_scarcity_usd_per_mwh: number | null;
  total_operating_capacity_mw: number;
  total_planned_re_capacity_mw: number;
}

export type BaselineMetric =
  | "carbon_intensity_kg_per_mwh"
  | "human_health_dalys_per_mwh"
  | "ecosystem_quality_species_yr_per_mwh"
  | "resource_scarcity_usd_per_mwh";

export interface BaselineSnapshotRegion {
  ba_code: string;
  total_generation_mwh: number | null;
  carbon_intensity_kg_per_mwh: number | null;
  human_health_dalys_per_mwh: number | null;
  ecosystem_quality_species_yr_per_mwh: number | null;
  resource_scarcity_usd_per_mwh: number | null;
  data_completeness: number;
  pipeline_generator_count: number;
  pipeline_capacity_mw: number;
  pipeline_dalys_avoided: number;
  pipeline_pdfs_avoided: number;
  pipeline_usd_avoided: number;
}

export interface BaselineSnapshotResponse {
  regions: BaselineSnapshotRegion[];
  count: number;
  period_type: string;
  pipeline_totals: {
    generator_count: number;
    capacity_mw: number;
    dalys_avoided: number;
    pdfs_avoided: number;
    usd_avoided: number;
  };
}

// ─── API Functions ───────────────────────────────────────────────────

export async function getBaselineSummary(
  baCode: string,
  periodType = "latest"
): Promise<BaselineSummary> {
  const res = await fetch(
    `${API}/grid/baseline/${encodeURIComponent(baCode)}?period_type=${periodType}`
  );
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getAllBaselines(
  periodType = "latest"
): Promise<BaselineSummary[]> {
  const res = await fetch(`${API}/grid/baseline?period_type=${periodType}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getBaselineRankings(
  metric: BaselineMetric = "carbon_intensity_kg_per_mwh",
  periodType = "latest"
): Promise<BARankingResponse> {
  const res = await fetch(
    `${API}/grid/rankings?metric=${metric}&period_type=${periodType}`
  );
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getStateBaselines(
  periodType = "latest"
): Promise<StateBaseline[]> {
  const res = await fetch(
    `${API}/grid/state-baselines?period_type=${periodType}`
  );
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getBaselineSnapshot(
  periodType = "latest"
): Promise<BaselineSnapshotResponse> {
  const res = await fetch(
    `${API}/grid/baseline-snapshot?period_type=${periodType}`
  );
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}
